import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Picture from '../components/Picture';
import AdminNav from '../components/AdminNav';
import logo from '../assets/images/logoFree.webp';
import logoFallback from '../assets/images/logoFree.png';
import '../styles/AdminLogsPage.css';
import LogsBarChart from '../components/charts/LogsBarChart';
import { clearAdminKey, getAdminKey } from '../utils/adminAuth';

const ANALYTICS_ENDPOINT = '/.netlify/functions/analytics';
const LOG_DETAILS_ENDPOINT = '/.netlify/functions/log-details';
const EMPTY_ARRAY = [];
const MAX_LOG_BUCKETS = 2000;
const LOG_DETAILS_LIMIT = 400;
const VISITOR_LABELS_ENDPOINT = '/.netlify/functions/visitor-label';
const EXCLUDED_VISITORS_STORAGE_KEY = 'analyticsExcludedVisitors';

const PRESET_RANGE_OPTIONS = [
    { value: '30m', label: '30 dernières minutes', ms: 30 * 60 * 1000 },
    { value: '1h', label: 'Dernière heure', ms: 60 * 60 * 1000 },
    { value: '3h', label: 'Dernières 3 heures', ms: 3 * 60 * 60 * 1000 },
    { value: '12h', label: 'Dernières 12 heures', ms: 12 * 60 * 60 * 1000 },
    { value: '1d', label: '1 jour', ms: 24 * 60 * 60 * 1000 },
    { value: '3d', label: '3 jours', ms: 3 * 24 * 60 * 60 * 1000 },
    { value: '7d', label: '7 derniers jours', ms: 7 * 24 * 60 * 60 * 1000 },
    { value: '14d', label: '14 derniers jours', ms: 14 * 24 * 60 * 60 * 1000 },
    { value: '30d', label: '30 derniers jours', ms: 30 * 24 * 60 * 60 * 1000 }
];

const PRESET_RANGE_MS = PRESET_RANGE_OPTIONS.reduce((acc, option) => {
    acc[option.value] = option.ms;
    return acc;
}, {});

const PRESET_RANGE_LABELS = PRESET_RANGE_OPTIONS.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
}, {});

PRESET_RANGE_LABELS.custom = 'Personnalisé';

const getPresetDurationMs = (value) => PRESET_RANGE_MS[value] || null;

const formatNumber = (value) => new Intl.NumberFormat('fr-FR').format(value || 0);

const formatPercent = (value) =>
    new Intl.NumberFormat('fr-FR', {
        style: 'percent',
        maximumFractionDigits: 1
    }).format(value || 0);

const formatDecimal = (value) =>
    new Intl.NumberFormat('fr-FR', {
        maximumFractionDigits: 1
    }).format(value || 0);

const formatVisitorId = (value) => {
    if (!value) return 'Inconnu';
    if (value.length <= 10) return value;
    return `${value.slice(0, 6)}...${value.slice(-4)}`;
};

const formatVisitorReference = (label, visitorId) => {
    const id = formatVisitorId(visitorId);
    return label ? `${label} (${id})` : id;
};

const formatDateTime = (value) => {
    if (value === null || value === undefined || value === '') return '-';

    const normalizedValue = typeof value === 'string' ? value.trim() : value;
    const looksLikeTimestamp =
        typeof normalizedValue === 'number' ||
        (typeof normalizedValue === 'string' && /^\d{8,}$/.test(normalizedValue));
    if (looksLikeTimestamp) {
        const date = new Date(Number(normalizedValue));
        if (!Number.isNaN(date.getTime())) {
            return new Intl.DateTimeFormat('fr-FR', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        }
    }

    const normalizedString = typeof normalizedValue === 'string' ? normalizedValue : String(normalizedValue);
    const normalized = normalizedString.includes('T')
        ? normalizedString
        : normalizedString.replace(' ', 'T');
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
};

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatDuration = (seconds) => {
    const s = Math.max(0, Math.floor(toNumber(seconds)));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}m ${String(r).padStart(2, '0')}s`;
};

const toTimestamp = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return value;
    const trimmed = String(value).trim();
    if (/^\d{8,}$/.test(trimmed)) return Number(trimmed);
    const normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const parseDateOnly = (value) => {
    if (!value) return null;
    const date = new Date(`${String(value)}T00:00:00.000Z`);
    return Number.isNaN(date.getTime()) ? null : date;
};

const truncateDateUtc = (date, interval) => {
    const copy = new Date(date.getTime());
    if (interval === 'day') {
        copy.setUTCHours(0, 0, 0, 0);
        return copy;
    }
    if (interval === 'hour') {
        copy.setUTCMinutes(0, 0, 0);
        return copy;
    }
    copy.setUTCSeconds(0, 0);
    return copy;
};

const addIntervalUtc = (date, interval) => {
    const copy = new Date(date.getTime());
    if (interval === 'day') {
        copy.setUTCDate(copy.getUTCDate() + 1);
        return copy;
    }
    if (interval === 'hour') {
        copy.setUTCHours(copy.getUTCHours() + 1);
        return copy;
    }
    copy.setUTCMinutes(copy.getUTCMinutes() + 1);
    return copy;
};

const formatBucketUtc = (date, interval) => {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    if (interval === 'day') {
        return `${year}-${month}-${day}`;
    }
    const hour = String(date.getUTCHours()).padStart(2, '0');
    if (interval === 'hour') {
        return `${year}-${month}-${day} ${hour}:00`;
    }
    const minute = String(date.getUTCMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hour}:${minute}`;
};

const getLogRange = ({ rangePreset, fromDate, toDate }) => {
    if (rangePreset === 'custom') {
        const start = parseDateOnly(fromDate);
        const end = parseDateOnly(toDate);
        if (!start || !end) return null;
        const until = new Date(end.getTime());
        until.setUTCDate(until.getUTCDate() + 1);
        return { start, end: until };
    }

    const durationMs = getPresetDurationMs(rangePreset);
    if (!durationMs) return null;
    const end = new Date();
    const start = new Date(end.getTime() - durationMs);
    return { start, end };
};

const estimateBucketCount = (range, interval) => {
    if (!range?.start || !range?.end) return 0;
    const ms = range.end.getTime() - range.start.getTime();
    if (!Number.isFinite(ms) || ms <= 0) return 0;
    const stepMs =
        interval === 'day'
            ? 24 * 60 * 60 * 1000
            : interval === 'hour'
              ? 60 * 60 * 1000
              : 60 * 1000;
    return Math.ceil(ms / stepMs);
};

const fillLogBuckets = (series, range, interval) => {
    if (!range?.start || !range?.end) return series;

    const normalized = Array.isArray(series) ? series : [];
    const values = new Map(
        normalized.map((item) => [item.bucket, toNumber(item.logs)])
    );

    const filled = [];
    let cursor = truncateDateUtc(range.start, interval);
    const end = range.end;
    while (cursor < end) {
        const bucket = formatBucketUtc(cursor, interval);
        const logs = values.get(bucket) ?? 0;
        filled.push({ bucket, logs });
        cursor = addIntervalUtc(cursor, interval);
    }
    return filled;
};

const toDatetimeLocalValue = (date) => {
    if (!date) return '';
    const offsetMs = date.getTimezoneOffset() * 60 * 1000;
    const local = new Date(date.getTime() - offsetMs);
    return local.toISOString().slice(0, 16);
};

const parseDatetimeLocalValue = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

function AdminLogsPage() {
    // preset vs custom
    const [rangePreset, setRangePreset] = useState('30d');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [logInterval, setLogInterval] = useState('day');
    const [eventsPerMode, setEventsPerMode] = useState('visitor');
    const [showAllButtons, setShowAllButtons] = useState(false);
    const [showAllPages, setShowAllPages] = useState(false);
    const [expandedVisitors, setExpandedVisitors] = useState({});
    const [visitorDetails, setVisitorDetails] = useState({});
    const [visitorPage, setVisitorPage] = useState(1);
    const [visitorPageSize, setVisitorPageSize] = useState(10);
    const [visitorLabelEdits, setVisitorLabelEdits] = useState({});
    const [visitorLabelStatus, setVisitorLabelStatus] = useState({});
    const [editingVisitorId, setEditingVisitorId] = useState('');
    const [detailStart, setDetailStart] = useState('');
    const [detailEnd, setDetailEnd] = useState('');
    const [detailRangeDirty, setDetailRangeDirty] = useState(false);
    const [showLogDetails, setShowLogDetails] = useState(false);
    const [logDetails, setLogDetails] = useState([]);
    const [logDetailsStatus, setLogDetailsStatus] = useState('idle');
    const [logDetailsError, setLogDetailsError] = useState('');
    const [excludedVisitors, setExcludedVisitors] = useState(() => {
        if (typeof window === 'undefined') return [];
        try {
            const stored = window.localStorage.getItem(EXCLUDED_VISITORS_STORAGE_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            return [];
        }
    });
    const [selectedVisitorToExclude, setSelectedVisitorToExclude] = useState('');
    const excludedVisitorIds = useMemo(
        () => excludedVisitors.map((entry) => entry.visitor_id),
        [excludedVisitors]
    );
    const excludedVisitorKey = excludedVisitorIds.join(',');
    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            if (excludedVisitors.length) {
                window.localStorage.setItem(
                    EXCLUDED_VISITORS_STORAGE_KEY,
                    JSON.stringify(excludedVisitors)
                );
            } else {
                window.localStorage.removeItem(EXCLUDED_VISITORS_STORAGE_KEY);
            }
        } catch (error) {
            // ignore storage errors
        }
    }, [excludedVisitors]);

    const [data, setData] = useState(null);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');

    const [refreshCount, setRefreshCount] = useState(0);
    const [lastRefresh, setLastRefresh] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const adminKey = getAdminKey();
    const redirectToLogin = useCallback(() => {
        clearAdminKey();
        navigate('/admin', {
            replace: true,
            state: { from: location }
        });
    }, [location, navigate]);

    const periodLabel = useMemo(() => {
        if (rangePreset === 'custom' && fromDate && toDate) {
            return `Période : ${fromDate} → ${toDate}`;
        }
        return `Période : ${PRESET_RANGE_LABELS[rangePreset] ?? 'Dernière période'}`;
    }, [rangePreset, fromDate, toDate]);

    const queryString = useMemo(() => {
        const params =
            rangePreset === 'custom' && fromDate && toDate
                ? new URLSearchParams({ from: fromDate, to: toDate })
                : new URLSearchParams({ range: rangePreset });
        if (excludedVisitorKey) {
            params.set('exclude', excludedVisitorKey);
        }
        return params.toString();
    }, [rangePreset, fromDate, toDate, excludedVisitorKey]);



    useEffect(() => {
        const fetchAnalytics = async () => {
            setStatus('loading');
            setError('');
            try {
                const headers = adminKey ? { 'x-analytics-key': adminKey } : {};
                const response = await fetch(`${ANALYTICS_ENDPOINT}?${queryString}`, {
                    headers
                });
                if (response.status === 401) {
                    redirectToLogin();
                    return;
                }
                if (!response.ok) {
                    throw new Error('Impossible de charger les stats.');
                }
                const payload = await response.json();
                setData(payload);
                setStatus('ready');
                setLastRefresh(new Date());
            } catch (fetchError) {
                setError(fetchError.message || 'Erreur inconnue.');
                setStatus('error');
            }
        };
        fetchAnalytics();
    }, [queryString, adminKey, refreshCount, redirectToLogin]);

    const handleRefreshLogs = () => {
        setRefreshCount((prev) => prev + 1);
    };

    // ✅ TOUS les useMemo / calculs dérivés AVANT le return conditionnel

    const totals = data?.totals || {};
    const pageViews = toNumber(totals.page_views);
    const clicks = toNumber(totals.clicks);
    const totalEvents = toNumber(totals.total_events);
    const uniqueVisitors = toNumber(data?.uniqueVisitors);
    const uniqueSessions = toNumber(data?.uniqueSessions);

    const topPages = (data?.topPages || EMPTY_ARRAY).slice(0, 3);
    const topClicks = (data?.topClicks || EMPTY_ARRAY).slice(0, 3);
    const allPages = data?.allPages || EMPTY_ARRAY;
    const allClicks = data?.allClicks || EMPTY_ARRAY;
    const visitors = data?.visitors || EMPTY_ARRAY;
    const visitorOptions = useMemo(() => {
        const normalized = Array.isArray(visitors) ? visitors : [];
        return normalized
            .filter((visitor) => visitor?.visitor_id)
            .filter(
                (visitor) =>
                    !excludedVisitors.some(
                        (excluded) => excluded.visitor_id === visitor.visitor_id
                    )
            )
            .map((visitor) => ({
                visitor_id: visitor.visitor_id,
                label: visitor.label || ''
            }));
    }, [visitors, excludedVisitors]);
    const logRange = useMemo(
        () => getLogRange({ rangePreset, fromDate, toDate }),
        [rangePreset, fromDate, toDate]
    );
    const logRangeKey = useMemo(() => {
        if (!logRange?.start || !logRange?.end) return '';
        return `${logRange.start.getTime()}-${logRange.end.getTime()}`;
    }, [logRange?.start, logRange?.end]);

    useEffect(() => {
        if (!logRange?.start || !logRange?.end || detailRangeDirty) return;
        setDetailStart(toDatetimeLocalValue(logRange.start));
        setDetailEnd(toDatetimeLocalValue(logRange.end));
    }, [
        logRangeKey,
        detailRangeDirty,
        logRange?.start,
        logRange?.end
    ]);

    useEffect(() => {
        if (!logRangeKey) return;
        setDetailRangeDirty(false);
    }, [logRangeKey]);

    const detailRange = useMemo(() => {
        if (!logRange?.start || !logRange?.end) {
            return null;
        }
        const parsedStart = parseDatetimeLocalValue(detailStart);
        const parsedEnd = parseDatetimeLocalValue(detailEnd);
        const baseStart = parsedStart || logRange.start;
        const baseEnd = parsedEnd || logRange.end;
        const boundedStart = new Date(Math.max(baseStart.getTime(), logRange.start.getTime()));
        const boundedEnd = new Date(Math.min(baseEnd.getTime(), logRange.end.getTime()));
        if (boundedEnd <= boundedStart) {
            return logRange;
        }
        return { start: boundedStart, end: boundedEnd };
    }, [detailStart, detailEnd, logRange]);

    const logDisplayRange = detailRange || logRange;

    const detailRangeKey = useMemo(() => {
        if (!detailRange?.start || !detailRange?.end) return '';
        return `${detailRange.start.toISOString()}-${detailRange.end.toISOString()}`;
    }, [detailRange]);

    const logIntervalInfo = useMemo(() => {
        let effectiveInterval = logInterval;
        let warning = '';
        const count = estimateBucketCount(logDisplayRange, logInterval);
        if (count > MAX_LOG_BUCKETS) {
            if (logInterval === 'minute') {
                effectiveInterval = 'hour';
                warning =
                    'Le mode minute est limité pour éviter un graphique illisible : affichage automatique par heure.';
            } else if (logInterval === 'hour') {
                effectiveInterval = 'day';
                warning =
                    'Le mode heure est limité pour éviter un graphique illisible : affichage automatique par jour.';
            }
        }
        return { effectiveInterval, warning };
    }, [logInterval, logDisplayRange]);
    const logData = useMemo(() => {
        const logSeries = data?.logSeries || {};
        const interval = logIntervalInfo.effectiveInterval;
        const raw =
            interval === 'hour'
                ? logSeries.hourly || EMPTY_ARRAY
                : interval === 'minute'
                  ? logSeries.minutely || EMPTY_ARRAY
                  : logSeries.daily || EMPTY_ARRAY;
        return fillLogBuckets(raw, logDisplayRange, interval);
    }, [data, logIntervalInfo.effectiveInterval, logDisplayRange]);

    const detailRangeLabel =
        detailRange?.start && detailRange?.end
            ? `${formatDateTime(detailRange.start.toISOString())} → ${formatDateTime(
                  detailRange.end.toISOString()
              )}`
            : 'Sélectionnez une tranche horaire.';
    const lastRefreshLabel = lastRefresh ? formatDateTime(lastRefresh.toISOString()) : 'Jamais';

    useEffect(() => {
        if (!showLogDetails || !detailRange) return;

        let canceled = false;

        const fetchLogDetails = async () => {
            setLogDetailsStatus('loading');
            setLogDetailsError('');
            try {
                const params = new URLSearchParams({
                    from: detailRange.start.toISOString(),
                    to: detailRange.end.toISOString(),
                    limit: String(LOG_DETAILS_LIMIT),
                });
                if (excludedVisitorIds.length) {
                    params.set('exclude', excludedVisitorIds.join(','));
                }
                const headers = adminKey ? { 'x-analytics-key': adminKey } : {};
                const response = await fetch(`${LOG_DETAILS_ENDPOINT}?${params}`, {
                    headers
                });
                if (response.status === 401) {
                    redirectToLogin();
                    return;
                }
                if (!response.ok) {
                    throw new Error('Impossible de charger les logs détaillés.');
                }
                const payload = await response.json();
                if (canceled) return;
                setLogDetails(payload.items || []);
                setLogDetailsStatus('ready');
            } catch (fetchError) {
                if (canceled) return;
                setLogDetailsStatus('error');
                setLogDetailsError(fetchError.message || 'Erreur inconnue.');
            }
        };

        fetchLogDetails();

        return () => {
            canceled = true;
        };
    }, [
        showLogDetails,
        detailRangeKey,
        adminKey,
        detailRange,
        excludedVisitorKey,
        excludedVisitorIds,
        redirectToLogin
    ]);

    const durationMs =
        logDisplayRange?.start && logDisplayRange?.end
            ? logDisplayRange.end.getTime() - logDisplayRange.start.getTime()
            : 0;
    const effectiveDays = durationMs / (24 * 60 * 60 * 1000);
    const showDailyAverage = effectiveDays >= 1;
    const averageViews = showDailyAverage ? pageViews / effectiveDays : 0;
    const averageClicks = showDailyAverage ? clicks / effectiveDays : 0;

    const clickRate = pageViews ? clicks / pageViews : 0;
    const eventsPerVisitor = uniqueVisitors ? totalEvents / uniqueVisitors : 0;
    const eventsPerSession = uniqueSessions ? totalEvents / uniqueSessions : 0;

    const maxTopPages = Math.max(1, ...topPages.map((item) => toNumber(item.views)));
    const maxTopClicks = Math.max(1, ...topClicks.map((item) => toNumber(item.clicks)));

    const eventsPerValue = eventsPerMode === 'session' ? eventsPerSession : eventsPerVisitor;
    const eventsPerLabel = eventsPerMode === 'session' ? 'événements / session' : 'événements / visiteur';

    const sortedVisitors = useMemo(() => {
        const normalized = Array.isArray(visitors) ? visitors : [];
        return [...normalized].sort((a, b) => {
            const aTime = toTimestamp(a.last_seen);
            const bTime = toTimestamp(b.last_seen);
            return bTime - aTime;
        });
    }, [visitors]);

    const totalVisitorPages = Math.max(1, Math.ceil(sortedVisitors.length / visitorPageSize));
    const clampedVisitorPage = Math.min(visitorPage, totalVisitorPages);
    const paginatedVisitors = useMemo(() => {
        const startIndex = (clampedVisitorPage - 1) * visitorPageSize;
        return sortedVisitors.slice(startIndex, startIndex + visitorPageSize);
    }, [sortedVisitors, clampedVisitorPage, visitorPageSize]);

    useEffect(() => {
        setVisitorPage(1);
    }, [visitorPageSize]);

    const getVisitorLabelValue = (visitor) =>
        visitorLabelEdits[visitor.visitor_id] ?? visitor.label ?? '';

    const handleVisitorLabelChange = (visitorId, value) => {
        setVisitorLabelEdits((prev) => ({
            ...prev,
            [visitorId]: value
        }));
    };

    const updateVisitorLabelInData = (visitorId, label) => {
        setData((prev) => {
            if (!prev?.visitors) return prev;
            return {
                ...prev,
                visitors: prev.visitors.map((visitor) =>
                    visitor.visitor_id === visitorId
                        ? { ...visitor, label }
                        : visitor
                )
            };
        });
    };

    const startEditingVisitorLabel = (visitor) => {
        setEditingVisitorId(visitor.visitor_id);
        setVisitorLabelEdits((prev) => ({
            ...prev,
            [visitor.visitor_id]: visitor.label || ''
        }));
        setVisitorLabelStatus((prev) => ({
            ...prev,
            [visitor.visitor_id]: ''
        }));
    };

    const cancelEditingVisitorLabel = (visitorId) => {
        setEditingVisitorId('');
        setVisitorLabelEdits((prev) => {
            const next = { ...prev };
            delete next[visitorId];
            return next;
        });
    };

    const handleSaveVisitorLabel = async (visitor) => {
        const visitorId = visitor.visitor_id;
        const rawValue = getVisitorLabelValue(visitor);
        const trimmed = rawValue.trim();

        setVisitorLabelStatus((prev) => ({
            ...prev,
            [visitorId]: 'saving'
        }));

        try {
            const headers = {
                'Content-Type': 'application/json',
                ...(adminKey ? { 'x-analytics-key': adminKey } : {})
            };
            const response = await fetch(VISITOR_LABELS_ENDPOINT, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    visitorId,
                    label: trimmed
                })
            });

            if (response.status === 401) {
                redirectToLogin();
                return false;
            }

            if (!response.ok) {
                throw new Error('Impossible de sauvegarder le label.');
            }

            updateVisitorLabelInData(visitorId, trimmed);
            setVisitorLabelEdits((prev) => {
                const next = { ...prev };
                if (trimmed) {
                    next[visitorId] = trimmed;
                } else {
                    delete next[visitorId];
                }
                return next;
            });
            setVisitorLabelStatus((prev) => ({
                ...prev,
                [visitorId]: 'saved'
            }));
            setEditingVisitorId('');
            return true;
        } catch (fetchError) {
            setVisitorLabelStatus((prev) => ({
                ...prev,
                [visitorId]: 'error'
            }));
            return false;
        }
    };

    const buildVisitorQueryString = (visitorId) => {
        const params =
            rangePreset === 'custom' && fromDate && toDate
                ? { from: fromDate, to: toDate, visitorId }
                : { range: rangePreset, visitorId };
        return new URLSearchParams(params).toString();
    };

    const handleToggleVisitor = async (visitorId) => {
        setExpandedVisitors((prev) => ({
            ...prev,
            [visitorId]: !prev[visitorId]
        }));

        if (visitorDetails[visitorId]) {
            return;
        }

        setVisitorDetails((prev) => ({
            ...prev,
            [visitorId]: { status: 'loading', items: [], error: '' }
        }));

        try {
            const headers = adminKey ? { 'x-analytics-key': adminKey } : {};
            const response = await fetch(
                `/.netlify/functions/analytics-visitor?${buildVisitorQueryString(visitorId)}`,
                { headers }
            );
            if (response.status === 401) {
                redirectToLogin();
                return;
            }
            if (!response.ok) {
                throw new Error('Impossible de charger les détails du visiteur.');
            }
            const payload = await response.json();
            setVisitorDetails((prev) => ({
                ...prev,
                [visitorId]: { status: 'ready', items: payload.items || [], error: '' }
            }));
        } catch (fetchError) {
            setVisitorDetails((prev) => ({
                ...prev,
                [visitorId]: {
                    status: 'error',
                    items: [],
                    error: fetchError.message || 'Erreur inconnue.'
                }
            }));
        }
    };

    const handleDetailStartChange = (value) => {
        setDetailRangeDirty(true);
        setDetailStart(value);
    };

    const handleDetailEndChange = (value) => {
        setDetailRangeDirty(true);
        setDetailEnd(value);
    };

    const resetDetailRange = () => {
        setDetailRangeDirty(false);
        setDetailStart('');
        setDetailEnd('');
    };

    const toggleLogDetails = () => {
        setShowLogDetails((prev) => {
            const next = !prev;
            if (!next) {
                setLogDetailsStatus('idle');
                setLogDetails([]);
                setLogDetailsError('');
            }
            return next;
        });
    };

    const handleAddExcludedVisitor = () => {
        if (!selectedVisitorToExclude) return;
        if (excludedVisitors.some((entry) => entry.visitor_id === selectedVisitorToExclude)) {
            return;
        }
        const match = visitors.find((item) => item.visitor_id === selectedVisitorToExclude);
        setExcludedVisitors((prev) => [
            ...prev,
            {
                visitor_id: selectedVisitorToExclude,
                label: match?.label || ''
            }
        ]);
        setSelectedVisitorToExclude('');
    };

    const handleRemoveExcludedVisitor = (visitorId) => {
        setExcludedVisitors((prev) =>
            prev.filter((entry) => entry.visitor_id !== visitorId)
        );
    };

    const groupSessions = (items) => {
        const map = new Map();
        items.forEach((item) => {
            const sessionId = item.session_id || 'inconnue';
            const timestamp = toTimestamp(item.created_at);
            if (!map.has(sessionId)) {
                map.set(sessionId, {
                    sessionId,
                    firstSeen: timestamp,
                    lastSeen: timestamp,
                    clicks: 0,
                    pageViews: 0,
                    items: []
                });
            }
            const entry = map.get(sessionId);
            entry.firstSeen = Math.min(entry.firstSeen, timestamp);
            entry.lastSeen = Math.max(entry.lastSeen, timestamp);
            if (item.event_type === 'click') entry.clicks += 1;
            if (item.event_type === 'page_view') entry.pageViews += 1;
            entry.items.push({
                ...item,
                timestamp
            });
        });

        return Array.from(map.values())
            .map((session) => ({
                ...session,
                items: session.items.sort((a, b) => a.timestamp - b.timestamp)
            }))
            .sort((a, b) => b.lastSeen - a.lastSeen);
    };

    if (!adminKey) {
        return (
            <Navigate
                to='/admin'
                replace
                state={{
                    from: location
                }}
            />
        );
    }

    return (
        <div className='page admin-analytics'>
            <div className='admin-nav-shell'>
                <AdminNav />
            </div>
            <main>
                <section className='hero admin-hero'>
                    <div className='container admin-hero-inner'>
                        <div className='admin-hero-copy'>
                            <div className='admin-brand'>
                                <Picture
                                    webpSrc={logo}
                                    fallbackSrc={logoFallback}
                                    alt='MAF Industries'
                                    className='admin-brand-logo'
                                    imgClassName='admin-brand-logo-img'
                                />
                                <div>
                                    <span className='admin-brand-title'>MAF Industries</span>
                                    <span className='admin-brand-subtitle'>Analytics interne</span>
                                </div>
                            </div>

                            <h1>Votre audience en un clin d'oeil.</h1>
                            <p className='lead'>
                                Vue claire sur le trafic, les clics et l'engagement. Tout reste privé et stocké
                                dans votre base.
                            </p>

                            <div className='admin-hero-actions admin-hero-actions--stats'>
                                <div className='admin-date-range'>
                                    <label className='admin-select'>
                                        <span>Date de début</span>
                                        <input
                                            type='date'
                                            value={fromDate}
                                            onChange={(event) => {
                                                setFromDate(event.target.value);
                                                setRangePreset('custom');
                                            }}
                                        />
                                    </label>
                                    <label className='admin-select'>
                                        <span>Date de fin</span>
                                        <input
                                            type='date'
                                            value={toDate}
                                            onChange={(event) => {
                                                setToDate(event.target.value);
                                                setRangePreset('custom');
                                            }}
                                        />
                                    </label>
                                </div>
                                <label className='admin-select'>
                                    <span>Auto-appliquer</span>
                                    <select
                                        value={rangePreset}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            setRangePreset(value);
                                            if (value !== 'custom') {
                                                setFromDate('');
                                                setToDate('');
                                            }
                                        }}
                                    >
                                        {PRESET_RANGE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                        <option value='custom'>Personnalisé</option>
                                    </select>
                                </label>
                            </div>

                            <div className='admin-hero-filters'>
                                <div className='admin-filter-group'>
                                    <label className='admin-select admin-select-inline'>
                                        <span>Masquer un visiteur</span>
                                        <div className='admin-filter-inline'>
                                            <select
                                                value={selectedVisitorToExclude}
                                                onChange={(event) =>
                                                    setSelectedVisitorToExclude(event.target.value)
                                                }
                                            >
                                                <option value=''>Choisir</option>
                                                {visitorOptions.map((option) => (
                                                    <option
                                                        key={option.visitor_id}
                                                        value={option.visitor_id}
                                                    >
                                                        {formatVisitorReference(
                                                            option.label,
                                                            option.visitor_id
                                                        )}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type='button'
                                                className='button button-outline'
                                                onClick={handleAddExcludedVisitor}
                                                disabled={!selectedVisitorToExclude}
                                            >
                                                Ajouter
                                            </button>
                                        </div>
                                    </label>
                                </div>
                                <div className='admin-filter-chips'>
                                    {excludedVisitors.map((visitor) => (
                                        <button
                                            key={visitor.visitor_id}
                                            type='button'
                                            className='admin-filter-chip'
                                            onClick={() =>
                                                handleRemoveExcludedVisitor(visitor.visitor_id)
                                            }
                                        >
                                            <span className='admin-filter-chip-text'>
                                                {formatVisitorReference(
                                                    visitor.label,
                                                    visitor.visitor_id
                                                )}
                                            </span>
                                            <span
                                                className='admin-filter-chip-remove'
                                                aria-hidden='true'
                                            >
                                                ×
                                            </span>
                                        </button>
                                    ))}
                                </div>
                                {excludedVisitors.length > 0 && (
                                    <p className='admin-muted admin-muted--small'>
                                        Les statistiques et les logs détaillés ignorent les visiteurs
                                        masqués ci-dessus.
                                    </p>
                                )}
                            </div>

                            <p className='admin-muted'>{periodLabel}</p>

                            {status === 'error' && <p className='admin-alert'>{error}</p>}
                            {status === 'loading' && <p className='admin-alert'>Chargement...</p>}
                        </div>

                        <div className='admin-hero-panel'>
                            <div className='admin-kpi-grid'>
                                <div className='admin-kpi-card'>
                                    <span>Pages vues</span>
                                    <strong>{formatNumber(pageViews)}</strong>
                                {showDailyAverage && (
                                    <small>{formatNumber(Math.round(averageViews))} / jour</small>
                                )}
                                </div>

                                <div className='admin-kpi-card'>
                                    <span>Clics</span>
                                    <strong>{formatNumber(clicks)}</strong>
                                {showDailyAverage && (
                                    <small>{formatNumber(Math.round(averageClicks))} / jour</small>
                                )}
                                </div>

                                <div className='admin-kpi-card'>
                                    <span>Visiteurs uniques</span>
                                    <strong>{formatNumber(uniqueVisitors)}</strong>
                                    <small>distincts</small>
                                </div>

                                <div className='admin-kpi-card'>
                                    <span>Sessions</span>
                                    <strong>{formatNumber(uniqueSessions)}</strong>
                                    <small>sessions uniques</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className='section'>
                    <div className='container admin-dashboard-grid admin-dashboard-grid--stats'>
                        <div className='admin-card admin-card-wide admin-logs-card'>
                            <div className='admin-card-header'>
                                <div>
                                    <p className='eyebrow'>Logs</p>
                                    <h2>Nombre d&apos;événements</h2>
                                </div>
                                <label className='admin-select'>
                                    <span>Affichage</span>
                                    <select
                                        value={logInterval}
                                        onChange={(event) => setLogInterval(event.target.value)}
                                    >
                                        <option value='day'>Par jour</option>
                                        <option value='hour'>Par heure</option>
                                        <option value='minute'>Par minute</option>
                                    </select>
                                </label>
                            </div>
                            {logIntervalInfo.warning && (
                                <p className='admin-alert'>{logIntervalInfo.warning}</p>
                            )}
                            <LogsBarChart
                                data={logData}
                                interval={logIntervalInfo.effectiveInterval}
                            />
                            <div className='admin-log-controls'>
                                <div className='admin-log-controls-range'>
                                    <label className='admin-select admin-select-inline'>
                                        <span>Tranche (de)</span>
                                        <input
                                            type='datetime-local'
                                            value={detailStart}
                                            onChange={(event) =>
                                                handleDetailStartChange(event.target.value)
                                            }
                                            disabled={!logRange?.start || !logRange?.end}
                                        />
                                    </label>
                                    <label className='admin-select admin-select-inline'>
                                        <span>Tranche (à)</span>
                                        <input
                                            type='datetime-local'
                                            value={detailEnd}
                                            onChange={(event) =>
                                                handleDetailEndChange(event.target.value)
                                            }
                                            disabled={!logRange?.start || !logRange?.end}
                                        />
                                    </label>
                                    <button
                                        type='button'
                                        className='button admin-log-reset'
                                        onClick={resetDetailRange}
                                        disabled={!logRange?.start || !logRange?.end}
                                    >
                                        Réinitialiser la tranche
                                    </button>
                                </div>
                                <div className='admin-log-controls-actions'>
                                    <button
                                        type='button'
                                        className='button button-outline'
                                        onClick={handleRefreshLogs}
                                    >
                                        Rafraîchir le graphique
                                    </button>
                                    <button
                                        type='button'
                                        className='admin-link-button'
                                        onClick={toggleLogDetails}
                                    >
                                        {showLogDetails
                                            ? 'Masquer les logs détaillés'
                                            : 'Afficher les logs détaillés'}
                                    </button>
                                    <span className='admin-muted admin-muted--small'>
                                        Dernière mise à jour : {lastRefreshLabel}
                                    </span>
                                    <span className='admin-muted admin-muted--small'>
                                        {detailRangeLabel}
                                    </span>
                                    <span className='admin-muted admin-muted--small'>
                                        (max {LOG_DETAILS_LIMIT} événements)
                                    </span>
                                </div>
                            </div>
                            {showLogDetails && (
                                <div className='admin-log-details'>
                                    {logDetailsStatus === 'loading' && (
                                        <p className='admin-alert'>Chargement des logs...</p>
                                    )}
                                    {logDetailsStatus === 'error' && (
                                        <p className='admin-alert'>{logDetailsError}</p>
                                    )}
                                    {logDetailsStatus === 'ready' && logDetails.length > 0 && (
                                        <div className='admin-log-details-table'>
                                            <table className='admin-table admin-table-log-details'>
                                                <thead>
                                                    <tr>
                                                        <th>Heure</th>
                                                        <th>Type</th>
                                                        <th>Page</th>
                                                        <th>Label</th>
                                                        <th>Visiteur</th>
                                                        <th>Label visiteur</th>
                                                        <th>Session</th>
                                                        <th>User-Agent</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {logDetails.map((item, index) => (
                                                        <tr key={`${item.created_at}-${index}`}>
                                                            <td>{item.created_at}</td>
                                                            <td>{item.event_type}</td>
                                                            <td>{item.path}</td>
                                                            <td>{item.label || '-'}</td>
                                                            <td>{item.visitor_id || '-'}</td>
                                                            <td>{item.visitor_label || '-'}</td>
                                                            <td>{item.session_id || '-'}</td>
                                                            <td>{item.user_agent || '-'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    {logDetailsStatus === 'ready' && !logDetails.length && (
                                        <p className='admin-alert'>Aucun log visible sur cette tranche.</p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className='admin-card'>
                            <div className='admin-card-header'>
                                <div>
                                    <p className='eyebrow'>Top 3</p>
                                    <h2>Boutons utilisés</h2>
                                </div>
                                <button
                                    type='button'
                                    className='admin-link-button'
                                    onClick={() => setShowAllButtons((prev) => !prev)}
                                >
                                    See more details
                                </button>
                            </div>
                            <div className='metric-list'>
                                {topClicks.map((item, index) => {
                                    const value = toNumber(item.clicks);
                                    const width = maxTopClicks === 0 ? 0 : (value / maxTopClicks) * 100;
                                    return (
                                        <div key={`${item.path}-${item.label}-${index}`} className='metric-row'>
                                            <div className='metric-label'>
                                                <span>
                                                    {item.label} <em>{item.path}</em>
                                                </span>
                                                <div className='metric-bar'>
                                                    <span style={{ width: `${width}%` }} />
                                                </div>
                                            </div>
                                            <strong>{formatNumber(value)}</strong>
                                        </div>
                                    );
                                })}
                                {!topClicks.length && (
                                    <p className='admin-alert'>Pas encore de clics enregistrés.</p>
                                )}
                            </div>
                        </div>

                        <div className='admin-card'>
                            <div className='admin-card-header'>
                                <div>
                                    <p className='eyebrow'>Top 3</p>
                                    <h2>Pages visitées</h2>
                                </div>
                                <button
                                    type='button'
                                    className='admin-link-button'
                                    onClick={() => setShowAllPages((prev) => !prev)}
                                >
                                    See more details
                                </button>
                            </div>
                            <div className='metric-list'>
                                {topPages.map((item) => {
                                    const value = toNumber(item.views);
                                    const width = maxTopPages === 0 ? 0 : (value / maxTopPages) * 100;
                                    return (
                                        <div key={item.path} className='metric-row'>
                                            <div className='metric-label'>
                                                <span>{item.path}</span>
                                                <div className='metric-bar'>
                                                    <span style={{ width: `${width}%` }} />
                                                </div>
                                            </div>
                                            <strong>{formatNumber(value)}</strong>
                                        </div>
                                    );
                                })}
                                {!topPages.length && <p className='admin-alert'>Pas encore de pages vues.</p>}
                            </div>
                        </div>

                        <div className='admin-card'>
                            <div className='admin-card-header'>
                                <div>
                                    <p className='eyebrow'>Engagement</p>
                                    <h2>Taux & événements</h2>
                                </div>
                            </div>
                            <div className='admin-metric-stack'>
                                <div>
                                    <span>Taux de clic</span>
                                    <strong>{formatPercent(clickRate)}</strong>
                                </div>
                                <div>
                                    <span>{eventsPerLabel}</span>
                                    <strong>{formatDecimal(eventsPerValue)}</strong>
                                </div>
                            </div>
                            <label className='admin-select admin-select-inline'>
                                <span>Afficher</span>
                                <select
                                    value={eventsPerMode}
                                    onChange={(event) => setEventsPerMode(event.target.value)}
                                >
                                    <option value='visitor'>Événements / visiteur</option>
                                    <option value='session'>Événements / session</option>
                                </select>
                            </label>
                        </div>
                    </div>
                </section>

                {(showAllButtons || showAllPages) && (
                    <section className='section'>
                        <div className='container admin-details-section'>
                            {showAllButtons && (
                                <div className='admin-card admin-card-wide'>
                                    <div className='admin-card-header'>
                                        <div>
                                            <p className='eyebrow'>Détails</p>
                                            <h2>Tous les boutons</h2>
                                        </div>
                                    </div>
                                    <div className='admin-table-wrap'>
                                        <table className='admin-table'>
                                            <thead>
                                                <tr>
                                                    <th>Bouton</th>
                                                    <th>Path</th>
                                                    <th>Clics / visiteur</th>
                                                    <th>Clics / session</th>
                                                    <th>Total clics</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {allClicks.map((item, index) => (
                                                    <tr key={`${item.path}-${item.label}-${index}`}>
                                                        <td>{item.label}</td>
                                                        <td>{item.path}</td>
                                                        <td>{formatDecimal(item.avg_clicks_per_visitor)}</td>
                                                        <td>{formatDecimal(item.avg_clicks_per_session)}</td>
                                                        <td>{formatNumber(item.total_clicks)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {!allClicks.length && (
                                            <p className='admin-alert'>Pas encore de boutons suivis.</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {showAllPages && (
                                <div className='admin-card admin-card-wide'>
                                    <div className='admin-card-header'>
                                        <div>
                                            <p className='eyebrow'>Détails</p>
                                            <h2>Toutes les pages</h2>
                                        </div>
                                    </div>
                                    <div className='admin-table-wrap'>
                                        <table className='admin-table'>
                                            <thead>
                                                <tr>
                                                    <th>Page</th>
                                                    <th>Visites / visiteur</th>
                                                    <th>Visites / session</th>
                                                    <th>Total visites</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {allPages.map((item) => (
                                                    <tr key={item.path}>
                                                        <td>{item.path}</td>
                                                        <td>{formatDecimal(item.avg_views_per_visitor)}</td>
                                                        <td>{formatDecimal(item.avg_views_per_session)}</td>
                                                        <td>{formatNumber(item.total_views)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {!allPages.length && (
                                            <p className='admin-alert'>Pas encore de pages suivies.</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                <section className='section'>
                    <div className='container'>
                        <div className='admin-card admin-card-wide'>
                            <div className='admin-card-header'>
                                <div>
                                    <p className='eyebrow'>Visiteurs</p>
                                    <h2>Liste détaillée</h2>
                                    <p className='admin-muted'>
                                        Ajoutez un label pour reconnaître facilement certains visiteurs (stocké
                                        en base).
                                    </p>
                                </div>
                            </div>
                            <div className='admin-table-wrap'>
                                <table className='admin-table admin-table-visitors'>
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Sessions</th>
                                            <th>Temps moyen</th>
                                            <th>Clics</th>
                                            <th>Pages vues</th>
                                            <th>Dernière visite</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedVisitors.map((visitor) => (
                                            <React.Fragment key={visitor.visitor_id}>
                                                <tr
                                                    className='admin-table-row'
                                                    onClick={() => handleToggleVisitor(visitor.visitor_id)}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter' || event.key === ' ') {
                                                            event.preventDefault();
                                                            handleToggleVisitor(visitor.visitor_id);
                                                        }
                                                    }}
                                                    tabIndex={0}
                                                    role='button'
                                                    aria-expanded={Boolean(expandedVisitors[visitor.visitor_id])}
                                                >
                                                    <td>
                                                        <div className='visitor-id-stack'>
                                                            <span className='visitor-id'>
                                                                {visitor.label
                                                                    ? visitor.label
                                                                    : formatVisitorId(visitor.visitor_id)}
                                                            </span>
                                                            {visitor.label && (
                                                                <small className='visitor-id-hint'>
                                                                    {formatVisitorId(visitor.visitor_id)}
                                                                </small>
                                                            )}
                                                            <div className='visitor-id-actions'>
                                                                <button
                                                                    type='button'
                                                                    className='visitor-id-button'
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        startEditingVisitorLabel(visitor);
                                                                    }}
                                                                >
                                                                    Label
                                                                </button>
                                                            </div>
                                                            {editingVisitorId === visitor.visitor_id && (
                                                                <div className='visitor-label-editor'>
                                                                    <input
                                                                        className='visitor-label-input'
                                                                        type='text'
                                                                        value={getVisitorLabelValue(visitor)}
                                                                        placeholder='Ex : Client VIP'
                                                                        onChange={(event) =>
                                                                            handleVisitorLabelChange(
                                                                                visitor.visitor_id,
                                                                                event.target.value
                                                                            )
                                                                        }
                                                                        onClick={(event) => event.stopPropagation()}
                                                                        onKeyDown={(event) => {
                                                                            if (event.key === 'Enter') {
                                                                                event.preventDefault();
                                                                                handleSaveVisitorLabel(visitor);
                                                                            }
                                                                            if (event.key === 'Escape') {
                                                                                event.preventDefault();
                                                                                cancelEditingVisitorLabel(
                                                                                    visitor.visitor_id
                                                                                );
                                                                            }
                                                                            event.stopPropagation();
                                                                        }}
                                                                    />
                                                                    {visitorLabelStatus[visitor.visitor_id] ===
                                                                        'error' && (
                                                                        <small className='visitor-label-error'>
                                                                            Erreur d&apos;enregistrement
                                                                        </small>
                                                                    )}
                                                                    <small className='visitor-label-hint'>
                                                                        Entrée pour enregistrer · Échap pour annuler
                                                                    </small>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>{formatNumber(visitor.session_count)}</td>
                                                    <td>{formatDuration(visitor.avg_session_duration)}</td>
                                                    <td>{formatNumber(visitor.clicks)}</td>
                                                    <td>{formatNumber(visitor.page_views)}</td>
                                                    <td>{formatDateTime(visitor.last_seen)}</td>
                                                </tr>
                                                {expandedVisitors[visitor.visitor_id] && (
                                                    <tr className='admin-table-expand'>
                                                        <td colSpan={6}>
                                                            <div className='admin-session-block'>
                                                                {visitorDetails[visitor.visitor_id]?.status === 'loading' && (
                                                                    <p className='admin-alert'>Chargement...</p>
                                                                )}
                                                                {visitorDetails[visitor.visitor_id]?.status === 'error' && (
                                                                    <p className='admin-alert'>
                                                                        {visitorDetails[visitor.visitor_id]?.error}
                                                                    </p>
                                                                )}
                                                                {visitorDetails[visitor.visitor_id]?.status === 'ready' && (
                                                                    <div className='admin-session-list'>
                                                                        <div className='admin-session-header'>
                                                                            <span>Session</span>
                                                                            <span>Durée</span>
                                                                            <span>Clics</span>
                                                                            <span>Pages</span>
                                                                            <span>Dernière activité</span>
                                                                        </div>
                                                                        {groupSessions(
                                                                            visitorDetails[visitor.visitor_id]?.items || []
                                                                        ).map((session) => (
                                                                            <details
                                                                                key={session.sessionId}
                                                                                className='admin-session-item'
                                                                            >
                                                                                <summary>
                                                                                    <span>
                                                                                        Session {session.sessionId}
                                                                                    </span>
                                                                                    <span>
                                                                                        {formatDuration(
                                                                                            (session.lastSeen - session.firstSeen) /
                                                                                                1000
                                                                                        )}
                                                                                    </span>
                                                                                    <span>{formatNumber(session.clicks)} clics</span>
                                                                                    <span>
                                                                                        {formatNumber(session.pageViews)} pages
                                                                                    </span>
                                                                                    <span>{formatDateTime(session.lastSeen)}</span>
                                                                                </summary>
                                                                                <div className='admin-session-actions'>
                                                                                    <table className='admin-table admin-table-actions'>
                                                                                        <thead>
                                                                                            <tr>
                                                                                                <th>Heure</th>
                                                                                                <th>Type</th>
                                                                                                <th>Page</th>
                                                                                                <th>Libellé</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {session.items.map((item, index) => (
                                                                                                <tr key={`${item.created_at}-${index}`}>
                                                                                                    <td>{formatDateTime(item.created_at)}</td>
                                                                                                    <td>{item.event_type}</td>
                                                                                                    <td>{item.path}</td>
                                                                                                    <td>{item.label || '-'}</td>
                                                                                                </tr>
                                                                                            ))}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            </details>
                                                                        ))}
                                                                        {!visitorDetails[visitor.visitor_id]?.items?.length && (
                                                                            <p className='admin-alert'>
                                                                                Pas encore d&apos;actions enregistrées.
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                                {!paginatedVisitors.length && (
                                    <p className='admin-alert'>Pas encore de visiteurs enregistrés.</p>
                                )}
                                {sortedVisitors.length > 0 && (
                                    <div className='admin-pagination'>
                                        <div className='admin-pagination-info'>
                                            {sortedVisitors.length} visiteurs · page {clampedVisitorPage} /{' '}
                                            {totalVisitorPages}
                                        </div>
                                        <div className='admin-pagination-controls'>
                                            <label className='admin-select admin-select-inline'>
                                                <span>Par page</span>
                                                <select
                                                    value={visitorPageSize}
                                                    onChange={(event) =>
                                                        setVisitorPageSize(Number(event.target.value))
                                                    }
                                                >
                                                    <option value={5}>5</option>
                                                    <option value={10}>10</option>
                                                    <option value={20}>20</option>
                                                    <option value={50}>50</option>
                                                </select>
                                            </label>
                                            <div className='admin-pagination-buttons'>
                                                <button
                                                    type='button'
                                                    className='button button-outline'
                                                    onClick={() => setVisitorPage(1)}
                                                    disabled={clampedVisitorPage === 1}
                                                >
                                                    «
                                                </button>
                                                <button
                                                    type='button'
                                                    className='button button-outline'
                                                    onClick={() =>
                                                        setVisitorPage((prev) => Math.max(1, prev - 1))
                                                    }
                                                    disabled={clampedVisitorPage === 1}
                                                >
                                                    Précédent
                                                </button>
                                                <button
                                                    type='button'
                                                    className='button button-outline'
                                                    onClick={() =>
                                                        setVisitorPage((prev) =>
                                                            Math.min(totalVisitorPages, prev + 1)
                                                        )
                                                    }
                                                    disabled={clampedVisitorPage === totalVisitorPages}
                                                >
                                                    Suivant
                                                </button>
                                                <button
                                                    type='button'
                                                    className='button button-outline'
                                                    onClick={() => setVisitorPage(totalVisitorPages)}
                                                    disabled={clampedVisitorPage === totalVisitorPages}
                                                >
                                                    »
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}

export default AdminLogsPage;
